import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import {
    BudgetPhases,
    MANTY_ORDERED_MOVABLE_BUDGET_PHASES,
} from 'business/constants';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateBudgetProposal } from 'routes/BudgetModule/helpers/hooks/budgetProposals';

type Props = {
    budgetProposal: BudgetProposalT;
    open: boolean;
    setOpen: (arg1: boolean) => void;
};

export default function BudgetPhaseDialog({
    budgetProposal,
    open,
    setOpen,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [phase, setPhase] = useState(budgetProposal.phase);

    useEffect(() => {
        // Reset the select phase each time the dialog is opened
        setPhase(budgetProposal.phase);
    }, [open]);

    const queryClient = useQueryClient();

    const { mutate: updateBudgetProposal, isPending } = useUpdateBudgetProposal(
        { queryClient, bpId: budgetProposal.id },
    );

    const submit = () => {
        updateBudgetProposal({
            budgetProposalId: budgetProposal.id,
            budgetProposal: { phase },
        });

        setOpen(false);
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
            <DialogTitle>
                {t('budgetProposal.changePhaseOfBudgetProposal', {
                    budgetProposal: budgetProposal.name,
                })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('budgetProposal.choosePhase')}
                </DialogContentText>
                <Select
                    variant="standard"
                    value={phase}
                    onChange={(event) => {
                        setPhase(event.target.value as BudgetPhases);
                    }}
                >
                    {Object.values(MANTY_ORDERED_MOVABLE_BUDGET_PHASES).map(
                        (value) => (
                            <MenuItem key={value} value={value}>
                                {t(`budgetProposal.${value}`)}
                            </MenuItem>
                        ),
                    )}
                </Select>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={() => {
                        submit();
                        setOpen(false);
                    }}
                    variant="contained"
                    autoFocus
                    disabled={!phase || isPending}
                >
                    {t('common.validate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
